import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchPortalData,
  recordConsent,
  loadProgress,
  saveProgress,
  submitPortal,
  uploadDocumentForExtraction,
  acceptExtractionValue,
  PortalData,
  PortalApiError,
  AnswerMap,
  ExtractionResult,
} from '../lib/portalApi'

export type PortalStep = 'loading' | 'error' | 'welcome' | 'form' | 'submitted'

type PortalContextType = {
  token: string
  data: PortalData | null
  step: PortalStep
  error: string | null
  errorStatus: number | null
  isLoading: boolean
  startForm: () => Promise<void>
  answers: AnswerMap
  isSaving: boolean
  lastSavedAt: Date | null
  updateAnswer: (fieldId: string, value: string | number | null) => void
  submitForm: () => Promise<void>
  submittedAt: string | null
  // Story 5.1 — extraction IA
  extractions: ExtractionResult[]
  documentQualityScore: number | null
  extractionStatus: 'idle' | 'uploading' | 'done' | 'quota_exceeded' | 'error'
  uploadDocument: (file: File) => Promise<void>
  // Story 5.2 — validation des extractions IA
  acceptExtraction: (id: string, value: string) => Promise<void>
  rejectExtraction: (id: string, fieldId: string) => void
  hasUnreviewedExtractions: boolean
  // Story 3.3 — mode complément ciblé
  isComplementMode: boolean
  requestedFieldIdsSet: Set<string>
}

const PortalContext = createContext<PortalContextType | undefined>(undefined)

export function PortalProvider({
  token,
  children,
}: {
  token: string
  children: React.ReactNode
}) {
  const [data, setData] = useState<PortalData | null>(null)
  const [step, setStep] = useState<PortalStep>('loading')
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Auto-save state
  const [answers, setAnswers] = useState<AnswerMap>({})
  const answersRef = useRef<AnswerMap>({})  // ref synchronisée pour éviter les closures stale dans le debounce
  const [isSaving, setIsSaving] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Submission state
  const [submittedAt, setSubmittedAt] = useState<string | null>(null)

  // Story 5.1 — extraction IA
  const [extractions, setExtractions] = useState<ExtractionResult[]>([])
  const [documentQualityScore, setDocumentQualityScore] = useState<number | null>(null)
  const [extractionStatus, setExtractionStatus] = useState<
    'idle' | 'uploading' | 'done' | 'quota_exceeded' | 'error'
  >('idle')
  // Story 5.2 — suivi des extractions révisées (pour hasUnreviewedExtractions + reset re-upload)
  const [reviewedExtractionIds, setReviewedExtractionIds] = useState<Set<string>>(new Set())

  // CA-2 & CA-3 — Charger les données du portail au montage (déclenche le tracking openedAt)
  useEffect(() => {
    ;(async () => {
      try {
        const portalData = await fetchPortalData(token)
        setData(portalData)
        setStep('welcome')
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Erreur inconnue'
        setErrorStatus(e instanceof PortalApiError ? e.status : 500)
        setError(msg)
        setStep('error')
      }
    })()
  }, [token])

  // Cleanup du timer au démontage — évite setState sur composant démonté (CA-4)
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  // Story 3.3 — Set des IDs de champs présents dans le template (utilisé pour
  // filtrer les requestedFieldIds orphelins, cf. AC #11 — graceful degradation
  // si le template a été muté entre la demande de complément et l'accès).
  const templateFieldIds = useMemo(() => {
    const ids = new Set<string>()
    for (const section of data?.template.sections ?? []) {
      for (const field of section.fields ?? []) {
        if (field?.id) ids.add(field.id)
      }
    }
    return ids
  }, [data])

  // Story 3.3 — mode complément : dérivé de la présence d'un complementRequest
  // non null AVEC au moins un fieldId ciblé ET résolvable dans le template.
  // Une `ComplementRequest` à `requestedFieldIds: []` ou n'incluant que des
  // IDs orphelins ne doit pas basculer le portail en mode ciblé — sinon Thomas
  // voit le CTA « Compléter ma réponse » sans aucun champ à remplir.
  const requestedFieldIdsSet = useMemo(() => {
    const raw = data?.complementRequest?.requestedFieldIds
    if (!Array.isArray(raw)) return new Set<string>()
    return new Set(raw.filter((id) => templateFieldIds.has(id)))
  }, [data, templateFieldIds])

  // `isComplementMode` reste vrai même si tous les `requestedFieldIds` ont été
  // filtrés (orphelins) : le portail doit afficher l'état vide « Aucun champ
  // à compléter » plutôt que de basculer en mode initial (qui laisserait
  // l'utilisateur éditer tous les champs alors que le backend rejettera la
  // soumission en 422 — AC #10/#11 graceful degradation).
  const isComplementMode = useMemo(
    () =>
      data?.complementRequest != null &&
      Array.isArray(data.complementRequest.requestedFieldIds) &&
      data.complementRequest.requestedFieldIds.length > 0,
    [data]
  )

  // CA-6 — Enregistrer le consentement RGPD puis charger la progression et ouvrir le formulaire
  const startForm = useCallback(async () => {
    setError(null)
    setErrorStatus(null)
    setIsLoading(true)
    try {
      // Story 3.3 AC #5 — pas de re-consentement en cycle complément (déjà recueilli au 1er cycle)
      if (!isComplementMode) {
        await recordConsent(token)
      }
      // CA-6 — Charger la progression existante après consentement (non-critique)
      // Isolé dans son propre try/catch : une erreur réseau sur loadProgress ne bloque
      // pas l'accès au formulaire — le fournisseur commence avec {} comme état initial
      try {
        const progressData = await loadProgress(token)
        if (Object.keys(progressData.answers).length > 0) {
          answersRef.current = progressData.answers  // synchroniser la ref AVANT setState
          setAnswers(progressData.answers)
        }
      } catch {
        /* loadProgress non-critique — formulaire accessible même sans progression récupérée */
      }
      setStep('form')
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Erreur lors de l'enregistrement du consentement"
      setErrorStatus(e instanceof PortalApiError ? e.status : 500)
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [token, isComplementMode])

  // CA-4 — Auto-save avec debounce 2s, answersRef pour éviter closures stale
  const updateAnswer = useCallback(
    (fieldId: string, value: string | number | null) => {
      answersRef.current = { ...answersRef.current, [fieldId]: value }  // synchrone — toujours frais
      setAnswers({ ...answersRef.current })
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(async () => {
        setIsSaving(true)
        try {
          await saveProgress(token, answersRef.current)
          setLastSavedAt(new Date())
        } catch {
          /* erreur silencieuse — auto-save non critique */
        } finally {
          setIsSaving(false)
        }
      }, 2000)
    },
    [token]
  )

  // Story 5.1 — Upload document et extraction IA
  const uploadDocument = useCallback(async (file: File) => {
    setExtractionStatus('uploading')
    setReviewedExtractionIds(new Set())  // Story 5.2 — reset avant nouvelle extraction
    try {
      const res = await uploadDocumentForExtraction(token, file)
      setExtractions(res.extractions)
      setDocumentQualityScore(res.documentQualityScore)
      setExtractionStatus('done')
    } catch (e: unknown) {
      if (e instanceof PortalApiError && e.status === 429) {
        setExtractionStatus('quota_exceeded')
      } else {
        setExtractionStatus('error')
        throw e // DocumentUploadZone gère le toast
      }
    }
  }, [token])

  // Story 5.2 — Accepter une extraction IA : appel API + intégration dans l'AnswerMap
  const acceptExtraction = useCallback(async (id: string, value: string) => {
    const extraction = extractions.find(e => e.id === id)
    if (!extraction) return
    await acceptExtractionValue(token, id)
    updateAnswer(extraction.fieldId, value)
    setReviewedExtractionIds(prev => new Set([...prev, id]))
  }, [token, extractions, updateAnswer])

  // Story 5.2 — Rejeter une extraction IA : effacer la valeur du champ (pas d'appel serveur)
  const rejectExtraction = useCallback((id: string, fieldId: string) => {
    updateAnswer(fieldId, null)
    setReviewedExtractionIds(prev => new Set([...prev, id]))
  }, [updateAnswer])

  // Story 5.2 — true si au moins une extraction non encore confirmée/rejetée
  const hasUnreviewedExtractions = useMemo(
    () => extractions.length > 0 && extractions.some(e => !reviewedExtractionIds.has(e.id)),
    [extractions, reviewedExtractionIds]
  )

  // CA-3 — Soumettre le formulaire : annuler l'auto-save concurrent, forcer un flush des réponses
  // en attente, puis appeler l'API. Re-lance l'erreur — CertificationSummaryModal la capte localement.
  const submitForm = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    // Flush des réponses non-sauvegardées avant soumission — évite de soumettre des données
    // périmées en DB (race condition avec le debounce 2s de updateAnswer)
    await saveProgress(token, answersRef.current)
    const result = await submitPortal(token)
    if (result.submitted) {
      setSubmittedAt(result.submittedAt)
      setStep('submitted')
    }
  }, [token])

  const value = useMemo<PortalContextType>(
    () => ({
      token,
      data,
      step,
      error,
      errorStatus,
      isLoading,
      startForm,
      answers,
      isSaving,
      lastSavedAt,
      updateAnswer,
      submitForm,
      submittedAt,
      extractions,
      documentQualityScore,
      extractionStatus,
      uploadDocument,
      acceptExtraction,
      rejectExtraction,
      hasUnreviewedExtractions,
      isComplementMode,
      requestedFieldIdsSet,
    }),
    [token, data, step, error, errorStatus, isLoading, startForm, answers, isSaving, lastSavedAt, updateAnswer, submitForm, submittedAt, extractions, documentQualityScore, extractionStatus, uploadDocument, acceptExtraction, rejectExtraction, hasUnreviewedExtractions, isComplementMode, requestedFieldIdsSet]
  )

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
}

export function usePortal(): PortalContextType {
  const ctx = useContext(PortalContext)
  if (!ctx) throw new Error('usePortal must be used within PortalProvider')
  return ctx
}
